import React, { useState } from 'react'
import { makeStyles, Typography } from '@material-ui/core'
import type { Theme } from '@material-ui/core/styles'
import { useSnackbar } from 'notistack'
import EditIcon from '@material-ui/icons/Edit'
import LaunchIcon from '@material-ui/icons/Launch'
import FileCopyIcon from '@material-ui/icons/FileCopyOutlined'
import classNames from 'classnames'
import type { User } from '@paintscout/api'
import StarIcon from '@material-ui/icons/Star'
import type { StyleClasses } from '@paintscout/ui/styles'
import { ActionButton, Button, HtmlContent, IconButton, Paper } from '@paintscout/ui'

const useStyles = makeStyles<Theme, DetailsContainerProps>(
  (theme) => ({
    root: {},
    paper: {},
    detailsContainer: {
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'flex-start',
      paddingLeft: theme.spacing(2),
      boxShadow: 'none',
      border: 'none'
    },
    detailsHeader: {
      display: 'flex',
      flexDirection: 'row',
      justifyContent: 'space-between',
      width: '100%',
      alignItems: 'center',
      paddingBottom: theme.spacing(1),
      height: '3.5rem',
      paddingRight: theme.spacing(0.5)
    },
    detailsTitle: (props) => ({
      display: 'flex',
      flexDirection: 'row',
      alignItems: 'center',
      gap: theme.spacing(1),
      '& svg': {
        color: 'transparent',
        transition: 'color 0.2s ease-in-out'
      },
      '&:hover': {
        cursor: props.handleLink ? 'pointer' : 'default',
        '& svg': {
          color: theme.paintscout.palette.brand.Neutral.P85,
          background: 'none'
        }
      }
    }),
    detailsCopyIcon: {
      fontSize: '1rem',
      marginLeft: theme.spacing(1),
      marginRight: theme.spacing(1),
      color: theme.paintscout.palette.brand.Neutral.P80,
      opacity: 1,
      transition: 'opacity 0.2s',
      '@media (hover: hover) and (pointer: fine)': {
        opacity: 0
      }
    },
    detailsItem: {
      paddingBottom: theme.spacing(1.5)
    },
    detailsBody: {
      paddingBottom: theme.spacing(),
      position: 'relative'
    },
    noDetailsBody: {
      paddingBottom: theme.spacing(3),
      paddingTop: theme.spacing(3),
      justifyContent: 'center',
      display: 'flex',
      alignItems: 'center'
    },
    contactDocsButtonContainer: {
      position: 'relative',
      display: 'flex',
      flexDirection: 'row',
      justifyContent: 'flex-start',
      width: '100%',
      alignItems: 'center',
      gap: theme.spacing(2),
      maxWidth: '100%',
      overflow: 'hidden',
      overflowX: 'auto',
      borderBottom: `1px solid ${theme.paintscout.palette.brand.Neutral.P90}`,
      marginBottom: theme.spacing(2),
      padding: theme.spacing(2)
    },
    primaryEditButton: {
      '@media print': {
        display: 'none'
      }
    },
    secondaryEditButton: {
      position: 'absolute',
      top: 0,
      right: 0
    },
    button: {
      ...theme.typography.subtitle2,
      backgroundColor: theme.paintscout.palette.brand.Neutral.P96,
      color: theme.palette.text.primary,
      textTransform: 'none',
      border: `1px solid transparent`,
      '&:hover': {
        backgroundColor: theme.paintscout.palette.brand.Neutral.P96,
        border: `1px solid ${theme.paintscout.palette.primary}`,
        boxShadow: 'none'
      }
    },
    activeButton: {
      backgroundColor: theme.palette.primary.main,
      color: theme.palette.common.white,
      '&:hover': {
        backgroundColor: theme.palette.primary.dark
      },
      '&:active': {
        backgroundColor: theme.palette.primary.main
      },
      '&:focus': {
        backgroundColor: theme.palette.primary.main,
        border: `1px solid ${theme.paintscout.palette.primary}`,
        boxShadow: 'none'
      },
      '&:focus-visible': {
        backgroundColor: theme.palette.primary.main,

        border: `1px solid ${theme.paintscout.palette.primary}`,
        boxShadow: 'none'
      }
    },
    editIcon: {
      // padding: theme.spacing(0.5),
      // make the area that is colored when hovering not so large
      '&:hover': {
        cursor: 'pointer',
        color: theme.paintscout.palette.brand.Blue.Blue55,
        transition: 'color 0.2s ease-in-out',
        backgroundColor: theme.paintscout.palette.brand.Neutral.P96
      }
    },
    linkHover: {
      cursor: 'pointer',
      touchAction: 'manipulation',
      WebkitTapHighlightColor: 'transparent',
      '@media (hover: hover) and (pointer: fine)': {
        '&:hover': {
          color: theme.paintscout.palette.brand.Blue.Blue55
        }
      }
    },
    preferredStar: {
      color: '#f5a622',
      fontSize: '1rem',
      marginBottom: theme.spacing(0.25),
      marginLeft: theme.spacing(0.5)
    },
    innerHtml: {
      '& p': {
        margin: 0
      },
      '& ul': {
        margin: 0,
        paddingLeft: '1.5rem'
      }
    },
    actions: {
      minWidth: '110px'
    }
  }),
  { name: 'DetailsContainer' }
)

export interface DetailsContainerProps {
  classes?: StyleClasses<typeof useStyles>
  details: Details[]
  title?: string
  contactDetails?: Details[]
  handleEdit?: (type: string) => void
  handleLink?: () => void
  handleAction?: (action: string) => void
  actions?: { key: string; label: string; value: string }[]
  hideTitle?: boolean
  noDetailsText?: string
}

export type Details = {
  title: string
  value: string | JSX.Element
  icon?: JSX.Element
  star?: boolean
  canCopy?: boolean
  user?: User
  link?: () => void
  html?: boolean
  selectOptions?: DetailSelectOption[]
  selectedValue?: string
  onSelect?: (option: DetailSelectOption | null) => void
}

export type DetailSelectOption = {
  label: string
  value: string
  color?: string
  role?: string
}

function DetailsContainer(props: DetailsContainerProps) {
  const classes = useStyles(props)
  const {
    details,
    contactDetails,
    title = 'Details',
    hideTitle,
    handleEdit,
    handleLink,
    handleAction,
    actions = [],
    noDetailsText = 'No details found.'
  } = props
  const { enqueueSnackbar } = useSnackbar()
  const [currentHover, setCurrentHover] = useState(null)
  const handleMouseEnter = (index: number) => {
    setCurrentHover(index)
  }
  const handleMouseLeave = () => {
    setCurrentHover(null)
  }
  const handleCopy = (value: string, title: string) => {
    navigator.clipboard.writeText(value)
    enqueueSnackbar(`${title} Copied`, { variant: 'success' })
  }
  const [tabs, setTabs] = useState([
    {
      label: 'Deal Details',
      value: 'deal',
      active: true
    },
    {
      label: 'Contact Details',
      value: 'contact',
      active: false
    }
  ])

  function handleTabClick(value: string) {
    setTabs((prevTabs) =>
      prevTabs.map((tab) => ({
        ...tab,
        active: tab.value === value
      }))
    )
  }
  const currentDetails = tabs.find((tab) => tab.active)?.value === 'deal' ? details : contactDetails

  return (
    <Paper className={classes.paper}>
      {contactDetails && (
        <div className={classes.contactDocsButtonContainer}>
          {tabs.map((tab) => (
            <Button
              key={tab.value}
              variant="contained"
              onClick={() => handleTabClick(tab.value)}
              size="small"
              className={classNames({
                [classes.button]: true,
                [classes.activeButton]: tab.active
              })}
            >
              {tab.label}
            </Button>
          ))}
        </div>
      )}
      <Paper className={classes.detailsContainer}>
        {!contactDetails && !hideTitle && (
          <div className={classes.detailsHeader}>
            <div className={classes.detailsTitle} onClick={handleLink ? () => handleLink() : null}>
              <Typography variant={'h3'}>{title}</Typography>
              {handleLink && <LaunchIcon onClick={handleLink} />}
            </div>
            {handleEdit && (
              <IconButton
                onClick={() => handleEdit('details')}
                className={classNames(classes.primaryEditButton, 'details-container-edit-button')}
              >
                <EditIcon />
              </IconButton>
            )}
            {handleAction && (
              <ActionButton
                classes={{ menuItem: classes.actions }}
                data-testid="actions"
                size="medium"
                actions={actions}
                disabled={actions.length === 0}
                onActionClick={(ev, action) => handleAction(action)}
              />
            )}
          </div>
        )}
        {currentDetails.length === 0 && (
          <div className={classes.noDetailsBody}>
            <Typography variant={'subtitle1'}>{noDetailsText}</Typography>
          </div>
        )}
        {currentDetails.length > 0 && (
          <div className={classes.detailsBody}>
            {contactDetails && handleEdit && (
              <IconButton
                onClick={() => handleEdit(tabs.find((tab) => tab.active)?.value === 'deal' ? 'deal' : 'contact')}
                className={classes.secondaryEditButton}
              >
                <EditIcon className={classes.editIcon} />
              </IconButton>
            )}
            {currentDetails.map((detail, index) => (
              <div
                key={index}
                onMouseEnter={() => handleMouseEnter(index)}
                onMouseLeave={handleMouseLeave}
                onClick={
                  detail.canCopy && !detail.link
                    ? () => handleCopy(detail.value as string, detail.title)
                    : detail.link
                      ? detail.link
                      : null
                }
                className={classNames({
                  [classes.detailsItem]: true,
                  [classes.linkHover]: detail.link && !detail.canCopy
                })}
              >
                <Typography style={{ display: 'flex', alignItems: 'center' }} variant={'body2'} color="textSecondary">
                  {detail.title}
                  {detail?.icon ? detail.icon : null}
                  {!detail?.icon && detail?.star && <StarIcon className={classes.preferredStar} />}
                </Typography>
                <div style={{ display: 'flex', alignItems: 'center' }}>
                  {detail.link ? (
                    <button
                      type="button"
                      style={{
                        background: 'none',
                        border: 'none',
                        padding: 0,
                        margin: 0,
                        color: 'inherit',
                        font: 'inherit',
                        cursor: 'pointer'
                      }}
                      className={classes.linkHover}
                      onClick={(e) => {
                        e.stopPropagation()
                        detail.link()
                      }}
                      tabIndex={0}
                    >
                      <Typography
                        variant={'body1'}
                        align="left"
                        className={classNames({
                          [classes.linkHover]: detail?.link
                        })}
                      >
                        {detail.value}
                      </Typography>
                    </button>
                  ) : detail.html ? (
                    <HtmlContent className={classes.innerHtml} content={String(detail.value)} />
                  ) : (
                    <>
                      <Typography
                        variant={'body1'}
                        className={classNames({
                          [classes.linkHover]: detail?.link
                        })}
                      >
                        {detail.value}
                      </Typography>
                    </>
                  )}
                  {detail.canCopy && (
                    <FileCopyIcon
                      onClick={
                        detail.canCopy && detail.link
                          ? (e) => {
                              e.stopPropagation()
                              handleCopy(detail.value as string, detail.title)
                            }
                          : null
                      }
                      className={classes.detailsCopyIcon}
                      style={{
                        opacity: currentHover === index ? 1 : undefined
                      }}
                    />
                  )}
                  {!detail.canCopy && detail.link && (
                    <LaunchIcon
                      className={classes.detailsCopyIcon}
                      style={{
                        opacity: currentHover === index ? 1 : undefined
                      }}
                      onClick={(e) => {
                        e.stopPropagation()
                        detail.link()
                      }}
                      tabIndex={0}
                      role="button"
                    />
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </Paper>
    </Paper>
  )
}

export default DetailsContainer
